import React from 'react';
import PropTypes from 'prop-types';
import { Card } from '@mui/material';
import TableSidebarDrawer from './TableSidebarDrawer';
import Table from './Table';
import TableToolbar from './TableToolbar';
import TableSidebarActions from './TableSidebarActions';
import DetailDialog from './DetailDialog';
import _ from 'lodash';

/**
 * TableView component
 * props:
 *  - useCtx: context inlcuding rows, setRows, react-fetching-library
 *
 *  - dataGridProps: props to pass to DataGrid component including rows and columns
 *
 *  - sidebarActions: [Optional] Array of objects with the following structure:
 *    {
 *    name: 'action name',
 *    text: 'action text',
 *    icon: <Icon />
 *    }
 *
 *  - RowActionsComponent: [Optional] Component, that renders buttons on rows.
 *    MUST take rowData prop, from which it can extract row data
 *    and setDetailCallbackComponent prop, which RowActionsComponent can (optionally) use to set a component to be
 *    displayed in DetailDialog
 *
 *  - FilterComponent: [Optional] Component, that MUST take setData prop, which can be used to set filtered table data
 *
 *  - SidebarActionsComponent: [Optional] If sidebarActions are not wanted, this component, can be rendered instead
 *
 *  - ToolbarComponent: [Optional] Component, that will be rendered as a toolbar if needed. By default TableToolbar
 *
 *  - toolbarProps: [Optional] Props to pass to ToolbarComponent
 */

const TableView = ({ useCtx, dataGridProps, sidebarActions, RowActionsComponent, FilterComponent, SidebarActionsComponent, ToolbarComponent = TableToolbar, toolbarProps = {}, showStatePanel }) => {
  const ctx = useCtx();
  const [sidebarVisible, setSidebarVisible] = React.useState(true);
  const [detailDialogVisible, setDetailDialogVisible] = React.useState(false);
  // const [detailDialogData, setDetailDialogData] = React.useState({});
  const [detailCallbackComponent, setDetailCallbackComponent] = React.useState(<></>);

  // const [rows, setRows] = React.useState(dataGridProps.rows);


  React.useEffect(() => {
    setDetailDialogVisible(!_.isEmpty(ctx.rowData));
    if (_.isEmpty(ctx.rowData)) {
      setDetailCallbackComponent(<></>);
    } else if (ctx.detailDialog && !_.isEmpty(ctx.rowData)) {
      setDetailCallbackComponent(ctx.detailDialog);
    }
  }, [ctx.rowData]);

  const dataGridPropsToPass = RowActionsComponent ? {
    ...dataGridProps,
    columns: [
      { field: 'actions',
        headerName: '',
        width: 150,
        align: 'left',
        disableClickEventBubbling: true,
        resizable: true,
        disableReorder: true,
        renderCell: params => <RowActionsComponent
          rowData={params.row}
          setDetailCallbackComponent= {async component => {
            const data = await ctx.getDetails?.(params.row.id);
            if (_.isNil(data)) {
              ctx.setRowData(params.row);
              setDetailCallbackComponent(component);
            } else if (!_.isEmpty(data)) {
              ctx.setRowData(data);
              setDetailCallbackComponent(component);
            }
          }} /> },
      ...ctx.columns
    ],
    rows: ctx.rows,
    loading: ctx.loading
  } : {
    ...dataGridProps,
    rows: ctx.rows,
    loading: ctx.loading
  };

  return <Card sx={{ height: { md: '72vh' }, display: { md: 'flex' } }}>
    { Boolean(sidebarActions || FilterComponent) && sidebarVisible &&
        <TableSidebarDrawer
          isOpen = {sidebarVisible}
          onClose={() => setSidebarVisible(false)}
          actionsComponent = {sidebarActions ? <TableSidebarActions actions={sidebarActions} refreshTableContent={() => ctx.query?.()}/> : <SidebarActionsComponent /> }
          filterComponent = {FilterComponent && <FilterComponent setData = {data => ctx.setRows(data)} />}
        />}

    <Table dataGridProps={dataGridPropsToPass}
      toolbarProps =
        {{ openSidebar: () => setSidebarVisible(true),
          // Name of the DataGridToolbar is misleading - should be toogleSidebar
          hideSidebar: () => setSidebarVisible(!sidebarVisible),
          showAll: true,
          searchCallback: search => {
            ctx.setSearch(search);
          },
          ...toolbarProps }}


      toolbarComponent = {ToolbarComponent}
      ctx={ctx}
    />
    {RowActionsComponent && <DetailDialog
      isOpen={detailDialogVisible}
      onClose={() => {
        ctx.setRowData({});
        ctx.query?.();
      }}
      objectData={ctx.rowData}
      DetailCallbackComponent={detailCallbackComponent}
      showStatePanel={showStatePanel}
      handleChangeStateCallback={ctx.handleChangeStateCallback} />}
  </Card>;
};

TableView.propTypes = {
  sidebarActions: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    text: PropTypes.string.isRequired,
    icon: PropTypes.element.isRequired,
    onClick: PropTypes.func.isRequired
  })),
  SidebarActionsComponent: PropTypes.element,
  dataGridProps: PropTypes.object.isRequired,
  ToolbarComponent: PropTypes.element,
  toolbarProps: PropTypes.object,
  DetailCallbackComponent: PropTypes.element,
  FilterComponent: PropTypes.element,
  useCtx: PropTypes.func.isRequired
};

TableView.defaultProps = {
  ToolbarComponent: TableToolbar,
  toolbarProps: {}
};

export default TableView;
